import { Injectable } from '@angular/core';
import { AuthService } from '../auth/auth.service';
import { UserDto } from 'app/shared/apis/user/model/userDto';
import { TenantCompactDto } from 'app/shared/apis/user/model/tenantCompactDto';
import { Role } from 'app/shared/common/enums/role';

// ── Domain unions ─────────────────────────────────────────────────────────
// PermissionRole = backend permission tier (Role enum mirror, kept here for
// readability — the source of truth remains `app/shared/common/enums/role.ts`).
export type PermissionRole = 'Owner' | 'SuperAdmin' | 'Admin' | 'TeamMember' | 'User';

// PersonaRole = frontend persona that drives sidebar nav, accent colour, and
// home route. SuperAdmin is a platform-wide privilege handled by RoleGuard,
// not a workspace persona.
export type PersonaRole = 'admin' | 'cro' | 'fs' | 'is';

// SESSION-meet-ux-009 / USET-005: `personalRole` is now typed on UserDto
// (hand-patched in userDto.ts alongside `territory`). This alias preserves
// the existing call signatures across this file but no longer needs to
// extend with the field — UserDto already carries it.
type UserDtoWithPersona = UserDto;

// ── Lookup tables (declarative; one source of truth per concern) ─────────
const PERMISSION_TO_PERSONA: Record<PermissionRole, PersonaRole> = {
  Owner:      'admin',
  SuperAdmin: 'admin',
  Admin:      'admin',
  TeamMember: 'is',
  User:       'is',
};

const HOME_ROUTE: Record<PersonaRole, string> = {
  admin:     '/admin',
  cro:       '/portfolio',
  fs:        '/deals',
  // SESSION-meet-ux-017 (Phase 1) / GAP-MEETUX-IS-001: IS persona's home is
  // now /home (the agent-driven IS home). /queue is still the primary action
  // surface and remains in the sidebar, but the persona lands on /home first.
  is:        '/home',
};

const VALID_PERSONAS: ReadonlySet<PersonaRole> = new Set<PersonaRole>(['admin', 'cro', 'fs', 'is']);

// Highest privilege first — a user with [TeamMember, Owner] should resolve as Owner.
const PERMISSION_PRIORITY: PermissionRole[] = ['SuperAdmin', 'Owner', 'Admin', 'TeamMember', 'User'];

// v2 cache key: old `personaRole` entries from the legacy resolver are ignored.
const STORAGE_KEY = 'personaRole_v2';

@Injectable({ providedIn: 'root' })
export class AppShellService {
  constructor(private authService: AuthService) {}

  /**
   * Read the cached persona; resolve from AuthService if no cache.
   */
  getPersonaRole(): PersonaRole {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached && this.isValidPersona(cached)) {
      return cached;
    }
    return this.resolveAndStampPersona(this.authService.user as UserDtoWithPersona);
  }

  /**
   * Resolve a persona for the given user. Precedence:
   *   1. Explicit `personalRole` on the User document (case-insensitive, validated).
   *   2. Permission-tier fallback via PERMISSION_TO_PERSONA, picking the
   *      highest-priority role present on the selected tenant.
   *   3. Default 'is'.
   * Stamps the result into localStorage.
   */
  resolveAndStampPersona(
    user: UserDtoWithPersona | undefined,
    selectedTenant?: TenantCompactDto
  ): PersonaRole {
    // (1) Explicit persona on the user document wins.
    const explicit = this.normalisePersona(user?.personalRole);
    if (explicit) {
      localStorage.setItem(STORAGE_KEY, explicit);
      return explicit;
    }

    // Backend ships either persona names ("Cro", "Fs", "Is", "Admin") or
    // permission tiers ("Owner", "SuperAdmin", "TeamMember", ...) in the same
    // Tenants[].Roles slot, with inconsistent casing. Normalise to lowercase
    // once, then try persona-shape first (it's the more specific signal) and
    // fall back to permission tier.
    const tenant = selectedTenant ?? this.pickActiveTenant(user);
    const rolesLower: string[] = (tenant?.roles ?? []).map(r => String(r).trim().toLowerCase());

    // (2) Persona-shaped string in Roles[] beats permission tier — same
    //     precedence rule as personalRole, just sourced from the Roles array.
    const personaFromRoles = rolesLower.map(r => this.normalisePersona(r)).find((p): p is PersonaRole => p !== null);
    if (personaFromRoles) {
      localStorage.setItem(STORAGE_KEY, personaFromRoles);
      return personaFromRoles;
    }

    // (3) Permission-tier fallback. Pick the highest-priority match.
    const topPermission = PERMISSION_PRIORITY.find(p => rolesLower.includes(p.toLowerCase()));
    const persona: PersonaRole = topPermission ? PERMISSION_TO_PERSONA[topPermission] : 'is';

    localStorage.setItem(STORAGE_KEY, persona);
    return persona;
  }

  /**
   * Where each persona lands after auth + tenant selection.
   */
  homeRouteForPersona(persona: PersonaRole): string {
    return HOME_ROUTE[persona];
  }

  setPersona(role: PersonaRole): void {
    localStorage.setItem(STORAGE_KEY, role);
  }

  clearPersona(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  private pickActiveTenant(user: UserDtoWithPersona | undefined): TenantCompactDto | undefined {
    if (!user?.tenants?.length) return undefined;
    return user.tenants[0];
  }

  private isValidPersona(v: string): v is PersonaRole {
    return VALID_PERSONAS.has(v as PersonaRole);
  }

  /**
   * Accept a persona string from any source (cache, document, query param).
   * Returns the canonical lowercase PersonaRole, or null if not recognised.
   */
  private normalisePersona(v: string | undefined): PersonaRole | null {
    if (!v) return null;
    const lower = String(v).trim().toLowerCase();
    return this.isValidPersona(lower) ? lower : null;
  }

}
